import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readDocument } from "@/office/word/document";

export const lektionAusrichtung: Lesson = {
  id: "ausrichtung",
  title: "Ausrichtung – und warum Blocksatz selten hilft",
  summary: "Die vier Ausrichtungen und wann welche wirklich passt.",
  durationMinutes: 7,
  learningGoals: [
    "Die vier Absatzausrichtungen unterscheiden",
    "Ausrichtung bewusst statt gewohnheitsmäßig wählen",
    "Die Nachteile von Blocksatz in Word einschätzen",
  ],
  steps: [
    {
      id: "vier-ausrichtungen",
      title: "Die vier Ausrichtungen",
      explanation: [
        "Nach Zeichen- und Formatvorlagen kommt jetzt die zweite Formatierungsebene: der " +
          "Absatz. Sie beginnt bei der Ausrichtung. Word kennt vier: linksbündig, " +
          "zentriert, rechtsbündig und Blocksatz.",
        "Linksbündig ist der Normalfall für Fließtext – die Wortabstände bleiben gleich " +
          "und das ausgefranste rechte Ende hilft dem Auge, die Zeile wiederzufinden. " +
          "Zentriert eignet sich für Titelseiten und Einladungen, rechtsbündig " +
          "hauptsächlich für Datumsangaben im Briefkopf.",
        "Auch hier gilt: Der Cursor muss nur im Absatz stehen, markieren ist nicht nötig.",
      ],
      keyPoints: [
        "Strg + L linksbündig, Strg + E zentriert, Strg + R rechtsbündig, Strg + B Blocksatz.",
        "Fließtext ist im Zweifel linksbündig richtig.",
      ],
      task: {
        instruction:
          "Schreibe eine Zeile als Überschrift und zentriere sie. Darunter kommt ein " +
          "Absatz Fließtext, der linksbündig bleibt.",
        hint: "Cursor in die Zeile setzen und Strg + E drücken.",
        check: async () => {
          const snapshot = await readDocument();
          if (snapshot.filledParagraphs.length < 2) {
            return fail(
              "Es braucht mindestens zwei Absätze mit Inhalt.",
              "Schreibe eine Zeile, drücke Enter und schreibe einen zweiten Absatz.",
            );
          }

          const zentriert = snapshot.filledParagraphs.find(
            (absatz) => absatz.format.alignment === "Centered",
          );
          const linksbuendig = snapshot.filledParagraphs.find(
            (absatz) =>
              absatz.format.alignment === "Left" &&
              zentriert !== undefined &&
              absatz.index > zentriert.index,
          );

          return fromCriteria(
            [
              criterion("Ein Absatz ist zentriert", Boolean(zentriert)),
              criterion("Darunter steht ein linksbündiger Absatz", Boolean(linksbuendig)),
            ],
            {
              success: "Genau so – zentriert für die Überschrift, linksbündig für den Text.",
              failure: "Die Ausrichtung passt noch nicht.",
              hint: "Der linksbündige Absatz muss unterhalb des zentrierten stehen.",
            },
          );
        },
      },
    },
    {
      id: "blocksatz",
      title: "Blocksatz und seine Tücken",
      explanation: [
        "Blocksatz zieht jede Zeile auf die volle Breite, indem er die Wortabstände " +
          "dehnt. In Büchern und Zeitungen sieht das gut aus – dort trennt der Satz " +
          "Wörter automatisch am Zeilenende.",
        "Word trennt standardmäßig nicht. Bei schmalen Spalten oder langen deutschen " +
          "Komposita entstehen dadurch riesige Löcher zwischen den Wörtern, im " +
          "schlimmsten Fall senkrechte weiße Bahnen quer durch den Absatz.",
        "Wenn Blocksatz sein muss, schalte die Silbentrennung dazu: Registerkarte " +
          "„Layout“ → „Silbentrennung“ → „Automatisch“. Ohne sie ist linksbündig fast " +
          "immer die bessere Wahl.",
      ],
      keyPoints: [
        "Blocksatz ohne Silbentrennung erzeugt Löcher im Text.",
        "Layout → Silbentrennung → Automatisch entschärft das Problem.",
        "Im Zweifel: linksbündig.",
      ],
      task: {
        instruction:
          "Probiere es aus: Setze deinen Fließtextabsatz einmal auf Blocksatz und " +
          "entscheide dich dann bewusst – behalte ihn oder stelle auf linksbündig zurück. " +
          "Beides wird als richtig gewertet.",
        hint: "Strg + B setzt Blocksatz, Strg + L wieder linksbündig.",
        check: async () => {
          const snapshot = await readDocument();
          const fliesstext = snapshot.filledParagraphs.filter(
            (absatz) => absatz.format.alignment === "Justified" || absatz.format.alignment === "Left",
          );

          if (fliesstext.length === 0) {
            return fail(
              "Es wurde kein Absatz im Blocksatz oder linksbündig gefunden.",
              "Setze den Cursor in deinen Fließtext und drücke Strg + B oder Strg + L.",
            );
          }

          const imBlocksatz = fliesstext.some((absatz) => absatz.format.alignment === "Justified");
          return pass(
            imBlocksatz
              ? "Blocksatz übernommen – denk an die Silbentrennung unter Layout."
              : "Linksbündig – die sichere Wahl für Fließtext.",
          );
        },
      },
    },
  ],
};
